import type { PublishAiSuggestionToKbPayload, PublishAiSuggestionToKbResult } from "../types/aiSuggestion";
import { apiRequest } from "./apiClient";

export type AiSuggestionReviewAction = "approve" | "reject" | "restore";

export type PostAiSuggestionReviewPayload = {
  readonly action: AiSuggestionReviewAction;
  readonly suggestion_item_id: string;
  readonly status: string;
  readonly suggestion_type: string;
  readonly original_text: string | null;
  readonly client_suggested_text: string | null;
  readonly ai_suggested_text: string | null;
  readonly approved_text: string | null;
  readonly recommended_clause: string | null;
  readonly business_unit_list: string[] | null;
  readonly metadata_json?: Record<string, unknown> | null;
  /** Approve only: run KB publish in the same request as the review (server merges transaction). */
  readonly publish_to_kb?: boolean;
  readonly confirm_bu_removal?: boolean;
};

export type AiSuggestionKbPublishError = {
  readonly code: string;
  readonly message: string;
};

export type AiSuggestionReviewResponse = {
  readonly id: string;
  readonly generated_contract_id: string;
  readonly action: string;
  readonly reviewed_by: string;
  readonly reviewed_at: string;
  readonly kb_publish: PublishAiSuggestionToKbResult | null;
  /** Present when publish_to_kb was true but server-side KB publish did not complete; review was still saved. */
  readonly kb_publish_error: AiSuggestionKbPublishError | null;
};

const asRecord = (v: unknown): Record<string, unknown> | null => {
  if (v !== null && typeof v === "object" && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }
  return null;
};

/**
 * POST /generated-contracts/{id}/ai-suggestions/review
 */
export const postAiSuggestionReview = async (
  generatedContractId: string,
  payload: PostAiSuggestionReviewPayload,
  options?: { readonly idempotencyKey?: string }
): Promise<AiSuggestionReviewResponse> => {
  const path = `/generated-contracts/${encodeURIComponent(generatedContractId)}/ai-suggestions/review`;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (options?.idempotencyKey?.trim()) {
    headers["Idempotency-Key"] = options.idempotencyKey.trim();
  }
  const response = await apiRequest(
    path,
    {
      method: "POST",
      headers,
      body: JSON.stringify({
        action: payload.action,
        suggestion_item_id: payload.suggestion_item_id,
        status: payload.status,
        suggestion_type: payload.suggestion_type,
        original_text: payload.original_text,
        client_suggested_text: payload.client_suggested_text,
        ai_suggested_text: payload.ai_suggested_text,
        approved_text: payload.approved_text,
        recommended_clause: payload.recommended_clause,
        business_unit_list: payload.business_unit_list,
        metadata_json: payload.metadata_json ?? null,
        publish_to_kb: payload.publish_to_kb ?? false,
        confirm_bu_removal: payload.confirm_bu_removal ?? false
      })
    },
    { requiresAuth: true, redirectOnUnauthorized: true }
  );

  const json: unknown = await response.json();
  const root = asRecord(json);
  const dataRaw = root?.data ?? json;
  const data = asRecord(dataRaw) ?? {};
  const id = data.id != null ? String(data.id) : "";
  const gcid = data.generated_contract_id != null ? String(data.generated_contract_id) : "";
  const kbRaw = data.kb_publish;
  const kbRec = kbRaw !== null && kbRaw !== undefined ? asRecord(kbRaw) : null;
  const readNum = (v: unknown): number | null => {
    if (v === null || v === undefined) return null;
    if (typeof v === "number" && !Number.isNaN(v)) return v;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };
  const kb_publish: PublishAiSuggestionToKbResult | null =
    kbRec && kbRec.approved_section_id != null
      ? {
          approved_section_id: String(kbRec.approved_section_id ?? ""),
          version_number: Number(kbRec.version_number ?? 0),
          global_version_number: Number(kbRec.global_version_number ?? 0),
          business_units: Array.isArray(kbRec.business_units)
            ? (kbRec.business_units as string[]).map(String)
            : [],
          match_kind: String(kbRec.match_kind ?? ""),
          match_score: readNum(kbRec.match_score),
          second_best_score: readNum(kbRec.second_best_score),
          ingestion_job_id: kbRec.ingestion_job_id != null ? String(kbRec.ingestion_job_id) : null
        }
      : null;
  const errRaw = data.kb_publish_error;
  const errRec = errRaw !== null && errRaw !== undefined ? asRecord(errRaw) : null;
  const kb_publish_error: AiSuggestionKbPublishError | null =
    errRec && errRec.code != null
      ? { code: String(errRec.code), message: String(errRec.message ?? "") }
      : null;
  return {
    id,
    generated_contract_id: gcid,
    action: String(data.action ?? ""),
    reviewed_by: String(data.reviewed_by ?? ""),
    reviewed_at: String(data.reviewed_at ?? ""),
    kb_publish,
    kb_publish_error
  };
};

/**
 * POST /generated-contracts/{id}/ai-suggestions/{itemId}/publish-to-kb
 */
export const postPublishAiSuggestionToKb = async (
  generatedContractId: string,
  suggestionItemId: string,
  payload: PublishAiSuggestionToKbPayload,
  options?: { readonly idempotencyKey?: string }
): Promise<PublishAiSuggestionToKbResult> => {
  const path = `/generated-contracts/${encodeURIComponent(generatedContractId)}/ai-suggestions/${encodeURIComponent(suggestionItemId)}/publish-to-kb`;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (options?.idempotencyKey?.trim()) {
    headers["Idempotency-Key"] = options.idempotencyKey.trim();
  }
  const response = await apiRequest(
    path,
    {
      method: "POST",
      headers,
      body: JSON.stringify({
        final_text: payload.final_text,
        business_units: [...payload.business_units],
        confirm_bu_removal: payload.confirm_bu_removal
      })
    },
    { requiresAuth: true, redirectOnUnauthorized: true }
  );

  const json: unknown = await response.json();
  const root = asRecord(json);
  const dataRaw = root?.data ?? json;
  const data = asRecord(dataRaw) ?? {};
  const readNum = (v: unknown): number | null => {
    if (v === null || v === undefined) return null;
    if (typeof v === "number" && !Number.isNaN(v)) return v;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };
  return {
    approved_section_id: String(data.approved_section_id ?? ""),
    version_number: Number(data.version_number ?? 0),
    global_version_number: Number(data.global_version_number ?? 0),
    business_units: Array.isArray(data.business_units) ? (data.business_units as string[]).map(String) : [],
    match_kind: String(data.match_kind ?? ""),
    match_score: readNum(data.match_score),
    second_best_score: readNum(data.second_best_score),
    ingestion_job_id: data.ingestion_job_id != null ? String(data.ingestion_job_id) : null
  };
};
